import { publicHref } from '../lib/publicUrls';
import { useEffect, useState } from 'react';
import { List } from 'lucide-react';

/**
 * Auto-builds a Table of Contents from H2/H3 inside the given containerSelector.
 * - Adds an `id` to each heading if missing (slugified text).
 * - Highlights the active heading on scroll.
 * - Sticky on desktop (lg+); collapsible on mobile.
 */
const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 64);

const TableOfContents = ({ containerSelector = '[data-testid="blog-body-content"]' }) => {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [openMobile, setOpenMobile] = useState(false);

  // Build TOC after content paints
  useEffect(() => {
    let mounted = true;
    const build = () => {
      const container = document.querySelector(containerSelector);
      if (!container) return;
      const heads = Array.from(container.querySelectorAll('h2, h3'));
      const nodes = heads
        .filter((h) => h.textContent.trim())
        .map((h) => {
          const text = h.textContent.trim();
          if (!h.id) h.id = slugify(text);
          return { id: h.id, text, level: h.tagName === 'H2' ? 2 : 3 };
        });
      if (mounted) setItems(nodes);
    };
    // Multiple paints for markdown render
    const t1 = setTimeout(build, 80);
    const t2 = setTimeout(build, 400);
    const t3 = setTimeout(build, 1000);
    return () => {
      mounted = false;
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [containerSelector]);

  // Active heading on scroll
  useEffect(() => {
    if (items.length === 0) return;
    const onScroll = () => {
      const scrollPadding = parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
      const offsets = items.map((it) => {
        const el = document.getElementById(it.id);
        return {
          id: it.id,
          top: el ? el.getBoundingClientRect().top : Infinity,
          threshold: scrollPadding + (el ? parseFloat(getComputedStyle(el).scrollMarginTop) || 0 : 0) + 2,
        };
      });
      const above = offsets.filter((o) => o.top <= o.threshold);
      const current = above.length > 0 ? above[above.length - 1].id : items[0].id;
      setActiveId(current);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [items]);

  if (items.length < 3) return null; // not worth rendering for tiny articles

  const handleClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.tabIndex = -1;
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }
    setOpenMobile(false);
  };

  return (
    <>
      {/* Mobile: floating button */}
      <button
        type="button"
        onClick={() => setOpenMobile((v) => !v)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-[#0072BB] text-white px-4 py-3 rounded-full shadow-lg flex items-center gap-2 font-medium"
        data-testid="toc-mobile-toggle"
      >
        <List size={18} />
        Contents
      </button>

      {openMobile && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setOpenMobile(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl p-6 max-h-[70vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="toc-mobile-panel"
          >
            <h4 className="font-bold text-lg mb-4">Table of Contents</h4>
            <ul className="space-y-2">
              {items.map((it) => (
                <li key={it.id} className={it.level === 3 ? 'pl-4' : ''}>
                  <a
                    aria-current={activeId === it.id ? 'location' : undefined}
                    href={publicHref(`#${it.id}`)}
                    onClick={(e) => handleClick(e, it.id)}
                    className={`block py-1.5 text-sm transition-colors ${
                      activeId === it.id ? 'text-[#0072BB] font-semibold' : 'text-gray-700 hover:text-[#0072BB]'
                    }`}
                    data-testid={`toc-link-${it.id}`}
                  >
                    {it.text}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Desktop: sticky sidebar */}
      <nav
        className="hidden lg:block sticky top-24 max-h-[calc(100vh-7rem)] overflow-y-auto pr-4"
        aria-label="Article navigation"
        data-testid="toc-desktop"
      >
        <div className="flex items-center gap-2 mb-4 text-xs uppercase tracking-wider text-gray-500 font-semibold">
          <List size={14} />
          On this page
        </div>
        <ul className="space-y-1 border-l-2 border-gray-200">
          {items.map((it) => (
            <li key={it.id} className={`${it.level === 3 ? 'pl-6' : 'pl-3'}`}>
              <a
                aria-current={activeId === it.id ? 'location' : undefined}
                href={publicHref(`#${it.id}`)}
                onClick={(e) => handleClick(e, it.id)}
                className={`block py-1.5 text-sm transition-colors -ml-0.5 border-l-2 pl-3 ${
                  activeId === it.id
                    ? 'border-[#0072BB] text-[#0072BB] font-semibold'
                    : 'border-transparent text-gray-600 hover:text-[#0072BB]'
                }`}
                data-testid={`toc-link-${it.id}`}
              >
                {it.text}
              </a>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
};

export default TableOfContents;
